// The global clan chat socket (R222).
//
// The members of a clan can play in different worlds on different game
// servers. A channel inside a world can
// therefore never carry a clan. The chat lives on the **meta server**, and
// the game connects to the clan's chat rather than the other way round.
//
// This is a second connection, beside `connection.ts`, and the two have
// nothing in common but the word "socket":
//
//   - `connection.ts` speaks binary protobuf to one game world. It dies
//     when the player moves to another world.
//   - this speaks JSON to the meta server. It survives that move, because
//     the clan does.
//
// The game protocol knows nothing about chat. Nothing here is protobuf, and
// nothing here belongs in `proto/aquaphobia.proto`.
//
// This module is the transport and a small state holder. It draws nothing:
// the chat furniture is the empire dock's business.

// The close codes the meta server sends, in the 4000-4999 private
// range. They come from the generated gen/balance.ts — the same
// data/contracts/protocol.toml table httpapi/clanchat.go is generated
// from (R359). They are deliberately not the game socket's 4001 and 4003
// (`connection.ts`): two sockets with two meanings for one number is a
// trap, and the generator refuses a collision.
//
// The body cap and the client's scrollback cap come from the same
// generated file — the data/product/meta.toml [meta.social] table the
// meta server's dbrepo is generated from (R333). The scrollback cap is
// re-exported, because `state.ts` caps the game's copy of the
// transcript with it.
import { runeCount } from '../util/text';
import {
  CHAT_BODY_LIMIT, CHAT_ENTRY_LIMIT, CLOSE_CHAT_NO_CLAN, CLOSE_CHAT_RATE_LIMITED,
  CLOSE_CHAT_UNAUTHORIZED, CLOSE_CHAT_UNAVAILABLE,
} from '../gen/balance';
import { CLAN_CHAT_BACKOFF_MAX_MS, CLAN_CHAT_BACKOFF_MS } from '../gen/presentation';
import { num, str } from './json';

export { CHAT_ENTRY_LIMIT };
import { LOCALE } from '../gen/strings';

/** Why the socket stopped for good. None of the three is fixed by
 * reconnecting: `unauthorized` needs a sign-in, `no-clan` needs a clan, and
 * `unavailable` needs the deployment to serve chat at all. */
export type ClanChatStop = 'unauthorized' | 'no-clan' | 'unavailable';

/** One line of the transcript. The server stamps `at` and names the sender;
 * a client sends neither. */
export interface ClanChatMessage {
  kind: 'message';
  seq: number;
  senderId: string;
  sender: string;
  body: string;
  /** Unix seconds, wall clock. The meta server hosts no world, so neither
   * the world clock nor the map clock of numbers.md §19 applies here. */
  at: number;
  /** A replayed line. A backlog is not news, so it must not count unread. */
  history: boolean;
}

/** The overflow marker: more was missed than the ring holds, and this many
 * lines are gone for good. The server sends it rather than dropping them
 * in silence. */
export interface ClanChatGap {
  kind: 'gap';
  missed: number;
  at: number;
}

/** One row of the transcript, in the order the server sent it. */
export type ClanChatEntry = ClanChatMessage | ClanChatGap;

/** The welcome that opens a session. */
export interface ClanChatWelcome {
  clanId: number;
  channelId: number;
  /** This account's public id, so the transcript can mark its own lines. */
  self: string;
  /** How many replayed messages follow. */
  backlog: number;
}

export interface ClanChatHandlers {
  onWelcome(w: ClanChatWelcome): void;
  onEntry(e: ClanChatEntry): void;
  /** One refused frame. The socket stays open. */
  onError(code: string, message: string): void;
  /** Whether the chat relay of the clan copies this chat to Discord
   *  (player-reach.md §3.10, R1641). The welcome states it, and a `relay`
   *  frame states a change; an older server states nothing, which is off. */
  onRelay(on: boolean): void;
  /** The socket opened or closed. */
  onStatus(connected: boolean): void;
  /** The socket stopped for good; nothing is scheduled. */
  onStop(kind: ClanChatStop): void;
}

/** True for a message the transcript holds already. The replay after a
 *  dropped frame can carry a line the live fan-out delivered, and the
 *  sequence number tells the two apart. A gap marker never repeats. */
export function repeatsLine(entries: readonly ClanChatEntry[], e: ClanChatEntry): boolean {
  return e.kind === 'message' && entries.some((x) => x.kind === 'message' && x.seq === e.seq);
}

/** The lines the game replays on a join, read or not (the hub asks for the
 * whole ring, which the server caps). */
export const GAME_CHAT_TAIL = 5;
export const HUB_CHAT_TAIL = 50;

/** Where the socket dials. `tail` asks for that many newest lines again.
 * Same-origin: nginx serves the bundle and proxies
 * `/api/` to the meta server, so the client needs no configuration. The
 * page language rides the query (R864, R2207): a WebSocket sets no
 * header, and the server never reads the browser's own language. */
export function clanChatUrl(tail = 0): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const q = new URLSearchParams();
  if (tail > 0) q.set('tail', String(tail));
  q.set('lang', LOCALE);
  return `${proto}//${location.host}/api/clan/chat?${q.toString()}`;
}

/** The clan chat socket. It reconnects with backoff, as `connection.ts`
 * does but on figures of its own (R3016), and it stops for good on the
 * three refusals a reconnect cannot mend. */
export class ClanChatConnection {
  private ws: WebSocket | null = null;
  private backoffMs = CLAN_CHAT_BACKOFF_MS;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped: ClanChatStop | null = null;
  /** When the next attempt fires, as a `performance.now()` stamp; null while
   * connected and null once the socket is stopped, since nothing is coming. */
  retryAt: number | null = null;
  /** Consecutive failed attempts since the socket was last open. */
  retries = 0;
  connected = false;

  constructor(private handlers: ClanChatHandlers, private url = clanChatUrl()) {
    this.connect();
  }

  /** Sends one message. It returns false when the socket is not open or the
   * body fails the cap, so a caller can keep the text in the box. */
  say(body: string): boolean {
    const trimmed = body.trim();
    if (trimmed === '' || runeCount(trimmed) > CHAT_BODY_LIMIT) return false;
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify({ type: 'say', body: trimmed }));
    return true;
  }

  /** Closes the socket on purpose. Nothing reconnects afterwards. */
  close() {
    this.stopped = this.stopped ?? 'unavailable';
    this.retryAt = null;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      try { ws.close(); } catch { /* already dead */ }
    }
    this.setConnected(false);
  }

  /** Whether the socket gave up, and why. */
  get stopReason(): ClanChatStop | null { return this.stopped; }

  private setConnected(on: boolean) {
    if (this.connected === on) return;
    this.connected = on;
    this.handlers.onStatus(on);
  }

  private stop(kind: ClanChatStop) {
    if (this.stopped !== null) return;
    this.stopped = kind;
    this.retryAt = null;
    this.setConnected(false);
    this.handlers.onStop(kind);
  }

  private connect() {
    if (this.stopped !== null) return;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.backoffMs = CLAN_CHAT_BACKOFF_MS;
      this.retryAt = null;
      this.retries = 0;
      this.setConnected(true);
    };
    ws.onclose = (ev: { code: number }) => {
      this.setConnected(false);
      switch (ev.code) {
        case CLOSE_CHAT_UNAUTHORIZED: this.stop('unauthorized'); return;
        case CLOSE_CHAT_NO_CLAN: this.stop('no-clan'); return;
        case CLOSE_CHAT_UNAVAILABLE: this.stop('unavailable'); return;
        case CLOSE_CHAT_RATE_LIMITED:
          // The address opened too many sockets. Dialling straight back is
          // what caused it, so the next attempt waits the full ceiling.
          this.backoffMs = CLAN_CHAT_BACKOFF_MAX_MS;
          break;
      }
      this.retries++;
      this.retryAt = performance.now() + this.backoffMs;
      this.timer = setTimeout(() => this.connect(), this.backoffMs);
      this.backoffMs = Math.min(this.backoffMs * 2, CLAN_CHAT_BACKOFF_MAX_MS);
    };
    ws.onmessage = (ev: { data: unknown }) => {
      if (typeof ev.data !== 'string') return;
      let frame: unknown;
      try {
        frame = JSON.parse(ev.data);
      } catch {
        // One bad frame must not wedge the socket. Drop it and read on.
        return;
      }
      this.dispatch(frame);
    };
  }

  private dispatch(frame: unknown) {
    if (typeof frame !== 'object' || frame === null) return;
    const f = frame as Record<string, unknown>;
    switch (f.type) {
      case 'welcome':
        this.handlers.onWelcome({
          clanId: num(f.clanId), channelId: num(f.channelId),
          self: str(f.self), backlog: num(f.backlog),
        });
        this.handlers.onRelay(f.relay === true);
        return;
      case 'relay':
        this.handlers.onRelay(f.relay === true);
        return;
      case 'message':
        this.handlers.onEntry({
          kind: 'message', seq: num(f.seq), senderId: str(f.senderId),
          sender: str(f.sender), body: str(f.body), at: num(f.at),
          history: f.history === true,
        });
        return;
      case 'gap':
        this.handlers.onEntry({ kind: 'gap', missed: num(f.missed), at: num(f.at) });
        return;
      case 'error':
        this.handlers.onError(str(f.code), str(f.message));
        return;
    }
  }
}

/**
 * The transcript, as the client holds it. It stores what the server sent
 * and computes nothing of its own — the same rule `state.ts` follows for
 * the game socket.
 */
export class ClanChatState {
  /** The transcript, oldest first, capped at CHAT_ENTRY_LIMIT. */
  entries: ClanChatEntry[] = [];
  /** The clan this transcript belongs to, or 0 before the welcome. */
  clanId = 0;
  /** This account's public id, so a view can mark its own lines. */
  selfId = '';
  /** Live messages since the last markRead. A replayed line never counts. */
  unread = 0;
  connected = false;
  /** Why the socket gave up, or null while it is still trying. */
  stopped: ClanChatStop | null = null;
  /** The last refusal the server sent, or null. */
  lastError: { code: string; message: string } | null = null;
  /** The chat relay of the clan copies this chat to Discord (R1641). */
  relay = false;

  private listeners: (() => void)[] = [];

  /** Subscribe to any change; returns an unsubscribe function. */
  subscribe(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }

  private changed() {
    for (const fn of this.listeners) fn();
  }

  /** The handlers to hand a ClanChatConnection. */
  handlers(): ClanChatHandlers {
    return {
      onWelcome: (w) => this.welcome(w),
      onEntry: (e) => this.entry(e),
      onError: (code, message) => {
        this.lastError = { code, message };
        this.changed();
      },
      onRelay: (on) => {
        if (this.relay === on) return;
        this.relay = on;
        this.changed();
      },
      onStatus: (on) => {
        this.connected = on;
        this.changed();
      },
      onStop: (kind) => {
        this.stopped = kind;
        this.connected = false;
        this.changed();
      },
    };
  }

  private welcome(w: ClanChatWelcome) {
    // A reconnect to another clan is a different transcript. The same clan
    // keeps what is already on screen, because the replay only carries what
    // this account has not seen.
    if (w.clanId !== this.clanId) this.entries = [];
    this.clanId = w.clanId;
    this.selfId = w.self;
    this.stopped = null;
    this.changed();
  }

  private entry(e: ClanChatEntry) {
    // A message the transcript already holds is not added twice: a replay
    // after a dropped frame can carry a line the live fan-out delivered.
    if (repeatsLine(this.entries, e)) return;
    this.entries.push(e);
    if (this.entries.length > CHAT_ENTRY_LIMIT) {
      this.entries = this.entries.slice(this.entries.length - CHAT_ENTRY_LIMIT);
    }
    if (e.kind === 'message' && !e.history) this.unread++;
    this.changed();
  }

  /** The reader has seen the transcript. */
  markRead() {
    if (this.unread === 0) return;
    this.unread = 0;
    this.changed();
  }
}
